// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { usePlanDetailContext } from './PlanDetailContext';
import { useSiteTime } from '../../../hooks/useSiteTime';
import { useFlagOn } from '../../../hooks/useFlagOn';
import PlanControlHandoff from './PlanControlHandoff';
import type { HitlApprovalState, HitlReviewSummary } from '../../../types/hitl';

type AuditTone = 'neutral' | 'ok' | 'reject';

const TONE_COLORS: Record<AuditTone, string> = {
  neutral: 'var(--text-secondary)',
  ok: 'var(--st-ok)',
  reject: 'var(--sev1fg)',
};

interface AuditRow {
  key: string;
  time: string;
  who: string;
  text: string;
  tone: AuditTone;
}

function toneFor(state: HitlApprovalState): AuditTone {
  if (state === 'APPROVED') return 'ok';
  if (state === 'REJECTED') return 'reject';
  return 'neutral';
}

/** The reviewer's free-text comment, if the review carries a string one. */
function reviewComment(review: HitlReviewSummary): string | undefined {
  const comment = review.attributes?.comment;
  return typeof comment === 'string' && comment.trim() ? comment : undefined;
}

function reviewText(review: HitlReviewSummary, expired: boolean): string {
  const comment = reviewComment(review);
  const note = comment ? ` Reviewer note: “${comment}”.` : '';
  if (review.state === 'APPROVED') return `Approved this review.${note}`;
  if (review.state === 'REJECTED') return `Rejected this review.${note}`;
  return expired
    ? 'The execution day passed before this review was decided.'
    : 'Waiting for Ops Manager Review';
}

function PlanAuditTrail() {
  const { plan, expired = false } = usePlanDetailContext();
  // The handoff steps are placeholders (no endpoint publishes handoff state).
  const showHandoff = useFlagOn('planControlHandoffEnabled');
  const { format } = useSiteTime();
  const decided = plan.status === 'APPROVED' || plan.status === 'REJECTED';
  const formatTimestamp = (value: string | undefined, fallback = '—') =>
    format(value, 'dateTime', fallback);
  const createdAt = formatTimestamp(plan.createdAt, plan.date);

  const rows: AuditRow[] = [
    {
      key: 'created',
      time: createdAt,
      who: 'EnergyFluo',
      text: `Plan created for site ${plan.dc_site_id} and quantified by the optimization engine.`,
      tone: 'neutral',
    },
  ];

  plan.reviews.forEach((review, i) => {
    const isReviewDecided = review.state === 'APPROVED' || review.state === 'REJECTED';
    rows.push({
      key: review.reviewId || `review-${i}`,
      time: isReviewDecided ? formatTimestamp(plan.review_date) : '—',
      who: 'Ops Manager',
      text: reviewText(review, expired),
      tone: toneFor(review.state),
    });
  });

  if (decided) {
    rows.push({
      key: 'decision',
      time: formatTimestamp(plan.review_date),
      who: 'Ops Manager',
      text: `Plan ${plan.status === 'APPROVED' ? 'approved' : 'rejected'} and written to EnergyFluo Audit.`,
      tone: plan.status === 'APPROVED' ? 'ok' : 'reject',
    });
  }

  return (
    <div className="pd-stack">
      <div className={`pd-audit-grid${showHandoff ? '' : ' pd-audit-grid--single'}`}>
        <div className="pd-audit">
          <div className="pd-audit-title">Audit trail</div>
          {rows.map(row => (
            <div className="pd-audit-row" key={row.key}>
              <span className="pd-audit-time">{row.time}</span>
              <span className="pd-audit-who" style={{ color: TONE_COLORS[row.tone] }}>
                {row.who}
              </span>
              <span className="pd-audit-text">{row.text}</span>
            </div>
          ))}
        </div>
        {showHandoff && <PlanControlHandoff />}
      </div>
    </div>
  );
}

export default PlanAuditTrail;
